import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll, AnimatePresence } from "framer-motion";
import { DeckScene } from "./DeckScene";
import { Eyebrow, EASE } from "./Motion";
import { getLenis } from "@/lib/lenis";

const LAYERS = [
  { id: "footings", t: "Concrete footings", d: "Poured below the frost line so your deck never heaves, sinks or settles." },
  { id: "posts", t: "Posts", d: "Pressure-treated posts on metal bases, plumbed and braced before anything else goes up." },
  { id: "beams", t: "Beams", d: "Doubled beams carry every pound from the joists down into the posts." },
  { id: "joists", t: "Joists", d: "Set 16\" on center with hangers and blocking, for a floor that feels solid underfoot." },
  { id: "decking", t: "Decking", d: "Composite or hardwood boards, hidden fasteners, gapped right for drainage." },
  { id: "railing", t: "Railing", d: "Built to code in aluminum, cable, wood or glass. The finishing line of the whole build." },
];

const clamp = (v) => Math.min(1, Math.max(0, v));
const stateFor = (p) => {
  const explode = p < 0.82 ? clamp((p - 0.04) / 0.18) : 1 - clamp((p - 0.84) / 0.12);
  const active = p < 0.24 || p > 0.82 ? -1 : Math.min(5, Math.floor(((p - 0.24) / 0.58) * 6));
  return { explode, active };
};

export const Explorer = () => {
  const ref = useRef(null);
  const state = useRef({ explode: 0, active: -1 });
  const [active, setActive] = useState(-1);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    state.current = stateFor(p);
    if (state.current.active !== active) setActive(state.current.active);
  });

  const jump = (i) => {
    const el = ref.current;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const target = top + (0.24 + (i + 0.5) / 6 * 0.58) * (el.offsetHeight - window.innerHeight);
    const l = getLenis();
    l ? l.scrollTo(target, { duration: 1.2 }) : window.scrollTo({ top: target, behavior: "smooth" });
  };

  const cur = LAYERS[active];

  return (
    <section id="explorer" ref={ref} data-testid="section-3d-blueprint" className="relative h-[420vh] bg-[#0A0A0A]">
      <div className="sticky top-0 flex h-screen items-center overflow-hidden">
        <div className="mx-auto grid w-full max-w-[1500px] items-center gap-6 px-5 pt-16 md:px-10 lg:grid-cols-12">
          <div className="relative z-10 lg:col-span-4">
            <Eyebrow>Anatomy of a Newland deck</Eyebrow>
            <h2 className="mt-5 font-display text-4xl font-bold leading-[1] tracking-tighter text-neutral-50 md:text-6xl">
              Take it apart.<br /><span className="font-serif font-normal italic text-neutral-400">See why it lasts.</span>
            </h2>
            <div className="mt-8 hidden border-t border-white/[0.08] md:block">
              {LAYERS.map((l, i) => (
                <button key={l.id} data-testid={`3d-layer-${l.id}`} onClick={() => jump(i)}
                  className={`flex w-full items-center justify-between border-b border-white/[0.08] py-3 text-left transition-colors ${active === i ? "text-white" : "text-neutral-600 hover:text-neutral-300"}`}>
                  <span className="flex items-baseline gap-4">
                    <span className="font-mono text-xs">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display text-xl font-bold tracking-tight">{l.t}</span>
                  </span>
                  <motion.span animate={{ scaleX: active === i ? 1 : 0 }} transition={{ duration: 0.5, ease: EASE }} className="h-px w-10 origin-left bg-white" />
                </button>
              ))}
            </div>
          </div>
          <div className="relative h-[52vh] md:h-[70vh] lg:col-span-8">
            <DeckScene state={state} testId="explorer-3d-canvas" className="absolute inset-0" />
            <AnimatePresence mode="wait">
              <motion.div key={cur ? cur.id : "all"} data-testid="explorer-spec-panel"
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45, ease: EASE }}
                className="pointer-events-none absolute bottom-2 left-0 right-0 border border-white/10 bg-[#0A0A0A]/80 p-5 backdrop-blur-xl md:bottom-6 md:left-auto md:right-6 md:w-[360px]">
                <div className="flex items-baseline justify-between">
                  <span className="eyebrow">{cur ? `Layer ${String(active + 1).padStart(2, "0")} / 06` : "Scroll to explode"}</span>
                </div>
                <div className="mt-3 font-display text-2xl font-bold tracking-tight text-white">{cur ? cur.t : "Six layers. Zero shortcuts."}</div>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{cur ? cur.d : "Keep scrolling and the deck comes apart layer by layer, from the footings up."}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
